import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/layouts/AdminLayout";
import { amdenNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, ShieldCheck, HeartPulse, Package, Upload, ArrowLeft, UserCheck, Download, Loader2, Trash2, SlidersHorizontal, Truck, Pencil, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { UsersUploadDialog } from "@/components/UsersUploadDialog";
import { ImportReviewDialog } from "@/components/ImportReviewDialog";
import { JobRolesManager } from "@/components/JobRolesManager";
import { ManagersPanel } from "@/components/ManagersPanel";

const TABS = [
  { key: "users", label: "Users", icon: Users },
  { key: "managers", label: "Managers", icon: UserCheck },
  { key: "platform", label: "Platform", icon: SlidersHorizontal },
  { key: "prsi", label: "PRSI Rules", icon: ShieldCheck },
  { key: "sick", label: "Sick Pay", icon: HeartPulse },
  { key: "costs", label: "Cost Types", icon: Package },
  { key: "suppliers", label: "Suppliers", icon: Truck },
  { key: "job_roles", label: "Job Roles", icon: Briefcase },
  { key: "import", label: "Import Data", icon: Upload },
];

export default function ClientManage() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { impersonate } = useAuth();
  const [company, setCompany] = useState(null);
  const [tab, setTab] = useState("users");
  const SC = { headers: { "X-Company-Id": cid } };

  const loadCompany = () => api.get(`/companies/${cid}`).then((r) => setCompany(r.data)).catch(() => {});
  useEffect(() => { loadCompany(); }, [cid]);

  return (
    <AdminLayout nav={amdenNav} title="Manage Company">
      <button onClick={() => nav("/amden/companies")} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-slate-900 mb-3" data-testid="back-to-companies"><ArrowLeft className="h-4 w-4" />All companies</button>
      <PageHeader title={company?.name || "Company"} subtitle={company ? `/${company.slug} · configure this client's platform` : ""}>
        {company && <div className="h-10 w-10 rounded-md flex items-center justify-center border border-zinc-200" style={{ backgroundColor: company.primary_color }}><BrandLogo company={company} className="h-7 max-w-[36px]" /></div>}
      </PageHeader>

      <div className="flex gap-1 border-b border-zinc-200 mb-6 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersTab cid={cid} SC={SC} impersonate={impersonate} nav={nav} />}
      {tab === "managers" && <ManagersPanel cid={cid} impersonate={impersonate} nav={nav} companyModules={company?.enabled_modules} />}
      {tab === "platform" && <PlatformTab cid={cid} SC={SC} company={company} reload={loadCompany} />}
      {tab === "prsi" && <PrsiTab SC={SC} />}
      {tab === "sick" && <SickTab SC={SC} />}
      {tab === "costs" && <CostsTab SC={SC} />}
      {tab === "suppliers" && <SuppliersTab SC={SC} />}
      {tab === "job_roles" && <JobRolesManager cfg={SC} />}
      {tab === "import" && <ImportTab cid={cid} />}
    </AdminLayout>
  );
}

function PlatformTab({ cid, SC, company, reload }) {
  const [limit, setLimit] = useState("");
  const [settings, setSettings] = useState(null);
  const [reqDesc, setReqDesc] = useState(false);
  useEffect(() => { setLimit(company?.user_limit ?? ""); setReqDesc(!!company?.require_line_description); }, [company]);
  useEffect(() => { api.get("/settings", SC).then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const toggleReqDesc = async (v) => {
    try { await api.put(`/companies/${cid}`, { require_line_description: v }); setReqDesc(v); toast.success("Saved"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const used = company?.stats?.users ?? 0;
  const saveLimit = async () => {
    try { await api.put(`/companies/${cid}`, { user_limit: limit === "" ? null : Number(limit) }); toast.success("Seat limit saved"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSite = async (restrict) => {
    try { await api.put("/settings", { open_site_selection: !restrict }, SC); setSettings((s) => ({ ...s, open_site_selection: !restrict })); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5 max-w-xl">
      <div className="bg-white border border-zinc-200 rounded-md p-5">
        <div className="font-heading font-bold text-slate-900 mb-1">User seats</div>
        <p className="text-sm text-zinc-500 mb-3">Maximum number of user accounts this client can create. Leave blank for unlimited.</p>
        <div className="text-sm mb-3" data-testid="seat-usage"><span className="font-bold">{used}</span> user{used === 1 ? "" : "s"} in use{limit !== "" && limit != null ? ` of ${limit} seats` : ""}</div>
        <div className="flex gap-2 items-end">
          <div><Label>Seat limit</Label><Input data-testid="seat-limit" type="number" min="0" placeholder="Unlimited" value={limit ?? ""} onChange={(e) => setLimit(e.target.value)} className="mt-1 w-40" /></div>
          <Button data-testid="seat-save" onClick={saveLimit} className="bg-slate-900 text-white">Save</Button>
        </div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-heading font-bold text-slate-900">Restrict timesheets to assigned sites</div>
            <p className="text-sm text-zinc-500 mt-1 max-w-md">When ON, employees can only log time on sites they're assigned to. When OFF, they can log time on any site.</p>
          </div>
          <Switch data-testid="site-restrict-toggle" checked={settings ? !settings.open_site_selection : false} onCheckedChange={(v) => toggleSite(v)} />
        </div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-heading font-bold text-slate-900">Require a description on every timesheet line</div>
            <p className="text-sm text-zinc-500 mt-1 max-w-md">When ON, this client's staff can't submit a week unless every work line has a description. Only Amden can change this — client admins can't.</p>
          </div>
          <Switch data-testid="require-desc-toggle" checked={reqDesc} onCheckedChange={(v) => toggleReqDesc(v)} />
        </div>
      </div>
    </div>
  );
}

function UsersTab({ cid, SC, impersonate, nav }) {
  const [rows, setRows] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "kqm_admin" });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "kqm_admin" });
  const [delUser, setDelUser] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const load = () => api.get("/users", SC).then((r) => setRows(r.data));
  const loadOrphans = () => api.get("/admin/orphan-check", SC).then((r) => setOrphans(r.data.orphans || [])).catch(() => {});
  useEffect(() => { load(); loadOrphans(); }, []);
  const repair = async (email) => {
    if (!window.confirm(`Relink ${email} to a staff record in this company and claim any unassigned timesheets they logged?`)) return;
    try { const r = await api.post("/admin/repair-user", { email, company_id: cid, adopt_orphans: true }); toast.success(`Fixed — ${(r.data.records_retagged || 0) + (r.data.orphans_adopted || 0)} record(s) updated`); load(); loadOrphans(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const create = async () => {
    try { await api.post("/users", { ...form, company_id: cid }, SC); toast.success("User created"); setDialog(false); setForm({ name: "", email: "", password: "", role: "kqm_admin" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (u) => { await api.put(`/users/${u.id}/${u.active ? "deactivate" : "activate"}`, {}, SC); load(); };
  const doImpersonate = async (u) => { try { await impersonate(u.id); toast.success("Support mode active"); nav(u.role === "employee" ? "/app" : u.role === "kqm_manager" ? "/manager" : "/admin"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.name} (${u.email}):`);
    if (!pw) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try { await api.put(`/users/${u.id}`, { password: pw }, SC); toast.success("Password updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const emailReset = async (u) => {
    try { const r = await api.post(`/users/${u.id}/email-reset-link`, {}, SC); toast.success(`Reset link emailed to ${r.data.email}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openEdit = (u) => { setEditUser(u); setEditForm({ name: u.name || "", email: u.email || "", role: u.role }); };
  const saveEdit = async () => {
    try { await api.put(`/users/${editUser.id}`, editForm, SC); toast.success("User updated"); setEditUser(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/users/${delUser.id}`, SC); toast.success("User deleted"); setDelUser(null); load(); loadOrphans(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      {orphans.length > 0 && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="orphan-banner">
          <div className="text-sm font-semibold text-amber-800 mb-2">{orphans.length} login{orphans.length === 1 ? "" : "s"} need attention</div>
          <div className="space-y-1">
            {orphans.map((o) => (
              <div key={o.user_id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate">{o.name || o.email} — <span className="text-amber-700">{o.issue}</span></span>
                <Button size="sm" className="h-7 bg-slate-900 text-white shrink-0" data-testid={`orphan-repair-${o.user_id}`} onClick={() => repair(o.email)}>Repair</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 mb-3">
        <Button data-testid="cm-upload-users" variant="outline" onClick={() => setUploadOpen(true)}><Upload className="h-4 w-4 mr-1" />Upload users</Button>
        <Button data-testid="cm-new-user" onClick={() => setDialog(true)} className="bg-slate-900 text-white">+ New user</Button>
      </div>
      <UsersUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} cfg={SC} onComplete={load} />
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} data-testid={`cm-user-${u.id}`} className="border-b border-zinc-50">
                <td className="p-3 font-medium">{u.name}</td><td className="p-3 text-zinc-500">{u.email}</td>
                <td className="p-3"><span className="text-xs bg-zinc-100 px-2 py-0.5 rounded">{u.role === "kqm_admin" ? "Client Admin" : u.role === "amden_super" ? "Amden Super" : u.role === "kqm_manager" ? "Manager" : "Employee"}</span></td>
                <td className="p-3">{u.active ? <span className="text-emerald-600 text-xs font-bold">Active</span> : <span className="text-zinc-400 text-xs font-bold">Disabled</span>}</td>
                <td className="p-3 text-right"><div className="flex flex-wrap gap-1 justify-end">
                  <Button size="sm" className="h-8 bg-amber-500 text-black font-bold" data-testid={`cm-impersonate-${u.id}`} onClick={() => doImpersonate(u)}><UserCheck className="h-3.5 w-3.5 mr-1" />Impersonate</Button>
                  <Button size="sm" variant="outline" className="h-8" data-testid={`cm-edit-${u.id}`} onClick={() => openEdit(u)}>Edit</Button>
                  {u.role === "employee" && <Button size="sm" variant="outline" className="h-8" data-testid={`cm-repair-${u.id}`} onClick={() => repair(u.email)}>Repair</Button>}
                  <Button size="sm" variant="outline" className="h-8" data-testid={`cm-reset-${u.id}`} onClick={() => resetPw(u)}>Reset PW</Button>
                  <Button size="sm" variant="outline" className="h-8" data-testid={`cm-email-reset-${u.id}`} onClick={() => emailReset(u)}>Email reset</Button>
                  <Button size="sm" variant="outline" className="h-8" data-testid={`cm-toggle-${u.id}`} onClick={() => toggle(u)}>{u.active ? "Disable" : "Enable"}</Button>
                  <Button size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`cm-delete-${u.id}`} onClick={() => setDelUser(u)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="cm-u-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="cm-u-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>Password</Label><Input data-testid="cm-u-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="cm-u-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="kqm_admin">Client Admin</SelectItem></SelectContent>
              </Select>
            </div>
            <Button data-testid="cm-u-submit" onClick={create} className="w-full bg-slate-900 text-white">Create user</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="cm-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="cm-edit-email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1" /></div>
            <div><Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="cm-edit-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="kqm_admin">Client Admin</SelectItem><SelectItem value="amden_super">Amden Super</SelectItem></SelectContent>
              </Select>
            </div>
            <Button data-testid="cm-edit-submit" onClick={saveEdit} className="w-full bg-slate-900 text-white">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete {delUser?.name || delUser?.email}?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">This permanently removes the login for <strong>{delUser?.email}</strong>. Any linked employee record is kept. This cannot be undone.</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDelUser(null)}>Cancel</Button>
            <Button data-testid="cm-delete-confirm" className="bg-rose-600 text-white hover:bg-rose-700" onClick={doDelete}>Delete permanently</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PrsiTab({ SC }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/prsi-classes", SC).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async (p, patch) => { try { await api.put(`/prsi-classes/${p.id}`, patch, SC); toast.success("PRSI rule saved"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4">
      <p className="text-xs text-zinc-500 mb-3">Low rate applies when weekly pay ≤ threshold; the higher rate applies above it.</p>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200"><th className="p-2">Class</th><th className="p-2">Employer %</th><th className="p-2">Low %</th><th className="p-2">Threshold €/wk</th><th className="p-2"></th></tr></thead>
        <tbody>{rows.map((p) => <PrsiRow key={p.id} p={p} onSave={save} />)}</tbody>
      </table>
    </div>
  );
}
function PrsiRow({ p, onSave }) {
  const [rate, setRate] = useState(p.employer_rate);
  const [low, setLow] = useState(p.low_rate ?? "");
  const [thr, setThr] = useState(p.threshold);
  const dirty = Number(rate) !== p.employer_rate || Number(thr) !== p.threshold || (low === "" ? p.low_rate != null : Number(low) !== p.low_rate);
  return (
    <tr data-testid={`cm-prsi-${p.id}`} className="border-b border-zinc-50">
      <td className="p-2 num font-medium">{p.prsi_class}</td>
      <td className="p-2"><Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="h-8 w-24" /></td>
      <td className="p-2"><Input type="number" value={low} onChange={(e) => setLow(e.target.value)} placeholder="—" className="h-8 w-24" /></td>
      <td className="p-2"><Input type="number" value={thr} onChange={(e) => setThr(e.target.value)} className="h-8 w-24" /></td>
      <td className="p-2 text-right">{dirty && <Button size="sm" data-testid={`cm-prsi-save-${p.id}`} className="h-8 bg-amber-500 text-black font-bold" onClick={() => onSave(p, { employer_rate: rate, low_rate: low === "" ? null : low, threshold: thr })}>Save</Button>}</td>
    </tr>
  );
}

function SickTab({ SC }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/sick-pay-rules", SC).then((x) => setR(x.data)); }, []);
  const F = [
    ["waiting_days", "Waiting days (before pay starts)"],
    ["statutory_days", "Statutory sick days / year"],
    ["statutory_pay_pct", "Statutory pay (% of wage)"],
    ["statutory_daily_cap", "Statutory daily cap (€)"],
    ["company_days", "Company top-up days / year"],
    ["company_pay_pct", "Company top-up pay (%)"],
  ];
  const save = async () => { try { await api.put("/sick-pay-rules", r, SC); toast.success("Sick pay rules saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (!r) return <div className="text-zinc-400">Loading…</div>;
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5 max-w-2xl">
      <div className="grid sm:grid-cols-2 gap-4">
        {F.map(([k, label]) => (
          <div key={k}><Label>{label}</Label><Input data-testid={`sick-${k}`} type="number" value={r[k]} onChange={(e) => setR({ ...r, [k]: e.target.value })} className="mt-1" /></div>
        ))}
      </div>
      <Button data-testid="sick-save" onClick={save} className="mt-5 bg-slate-900 text-white">Save sick pay rules</Button>
    </div>
  );
}

function CostsTab({ SC }) {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/cost-categories", SC).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (c) => { await api.put(`/cost-categories/${c.id}`, { enabled: c.enabled === false }, SC); load(); };
  const add = async () => { if (!name) return; await api.post("/cost-categories", { name }, SC); setName(""); toast.success("Cost type added"); load(); };
  const del = async (c) => { await api.delete(`/cost-categories/${c.id}`, SC); load(); };
  return (
    <div className="max-w-xl">
      <p className="text-sm text-zinc-500 mb-3">Turn cost types on or off for this client's site profitability & materials entry.</p>
      <div className="bg-white border border-zinc-200 rounded-md divide-y divide-zinc-50">
        {rows.map((c) => (
          <div key={c.id} data-testid={`cost-${c.id}`} className="flex items-center justify-between px-4 py-3">
            <span className={`text-sm ${c.enabled === false ? "text-zinc-400 line-through" : "font-medium"}`}>{c.name}</span>
            <div className="flex items-center gap-3">
              <Switch data-testid={`cost-toggle-${c.id}`} checked={c.enabled !== false} onCheckedChange={() => toggle(c)} />
              <button onClick={() => del(c)} data-testid={`cost-del-${c.id}`} className="text-zinc-300 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Input data-testid="cost-new-name" placeholder="New cost type…" value={name} onChange={(e) => setName(e.target.value)} />
        <Button data-testid="cost-add" onClick={add} className="bg-slate-900 text-white">Add</Button>
      </div>
    </div>
  );
}

function SuppliersTab({ SC }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", account_number: "" });
  const load = () => api.get("/suppliers/list", SC).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setEdit(null); setForm({ name: "", email: "", phone: "", account_number: "" }); setOpen(true); };
  const openEdit = (s) => { setEdit(s); setForm({ name: s.name || "", email: s.email || "", phone: s.phone || "", account_number: s.account_number || "" }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Supplier name is required"); return; }
    try {
      if (edit) await api.put(`/suppliers/${edit.id}`, form, SC);
      else await api.post("/suppliers", form, SC);
      toast.success(edit ? "Supplier updated" : "Supplier added"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"? This won't change past materials or credit notes.`)) return;
    try { await api.delete(`/suppliers/${s.id}`, SC); toast.success("Supplier deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between gap-4 mb-3">
        <p className="text-sm text-zinc-500">The managed supplier list for this client — used across materials & credit notes. Bulk-load names & contacts from the Import Data tab.</p>
        <Button data-testid="supplier-add-btn" onClick={openNew} className="bg-slate-900 text-white whitespace-nowrap">+ Add supplier</Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Account #</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} data-testid="suppliers-empty" className="p-6 text-center text-zinc-400">No suppliers yet. Add one or import a CSV.</td></tr>}
            {rows.map((s) => (
              <tr key={s.id} data-testid={`supplier-row-${s.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                <td className="p-3 font-medium text-slate-900">{s.name}</td>
                <td className="p-3 text-zinc-500">{s.email || "—"}</td>
                <td className="p-3 text-zinc-500">{s.phone || "—"}</td>
                <td className="p-3 text-zinc-500">{s.account_number || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button data-testid={`supplier-edit-${s.id}`} onClick={() => openEdit(s)} className="text-zinc-400 hover:text-slate-900 mr-3"><Pencil className="h-4 w-4 inline" /></button>
                  <button data-testid={`supplier-del-${s.id}`} onClick={() => del(s)} className="text-zinc-300 hover:text-rose-500"><Trash2 className="h-4 w-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{edit ? "Edit supplier" : "Add supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="sup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Contact email</Label><Input data-testid="sup-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>Phone</Label><Input data-testid="sup-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div><Label>Account number</Label><Input data-testid="sup-account" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="mt-1" /></div>
          </div>
          <Button data-testid="sup-save" onClick={save} className="w-full bg-slate-900 text-white mt-2">{edit ? "Save changes" : "Add supplier"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportTab({ cid }) {
  return (
    <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
      <ImportCard cid={cid} kind="employees" title="Employees" desc="Import your existing staff roster with pay rates & grades." />
      <ImportCard cid={cid} kind="sites" title="Sites" desc="Import past sites with budgets & charge rates. Customers are auto-created by name." />
      <ImportCard cid={cid} kind="suppliers" title="Suppliers" desc="Import the managed supplier list with contact email, phone & account number. Existing names are updated, new ones added." />
    </div>
  );
}
function ImportCard({ cid, kind, title, desc }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <div className="font-heading font-bold text-slate-900">{title}</div>
      <p className="text-sm text-zinc-500 mt-1 mb-4">{desc}</p>
      <Button size="sm" className="bg-slate-900 text-white" data-testid={`import-btn-${kind}`} onClick={() => setOpen(true)}><Upload className="h-3.5 w-3.5 mr-1" />Import CSV</Button>
      <ImportReviewDialog open={open} onOpenChange={setOpen} title={title} entityLabel={kind}
        templateUrl={`/import-template/${kind}`} importUrl={`/companies/${cid}/import/${kind}`}
        onComplete={() => toast.success(`${title} imported`)} />
    </div>
  );
}
